/*
 * CaptureCard — post-results email capture with inline opt-out enrichment.
 *
 * Port of prototype .capture-card (lines 3080–3164) and the success state
 * .cap-thanks (lines 889–905, showCaptureThanks at line 3635).
 * - Default state: follow_up_ok = true (checkbox ON).
 * - When unchecked, reveals the inline opt-out questions (team size,
 *   tools used, satisfaction 1–5) via .pb-cap-optout-questions.show.
 * - On submit, calls submitPlanContact() which re-POSTs with the same
 *   client_submission_id so the server upserts and bumps Version.
 * - On success, swaps form for the green cap-thanks panel and clears the
 *   localStorage draft. Copy varies by follow_up_ok and whether the
 *   buyer answered the opt-out questions.
 */
import { useEffect, useState } from 'react';
import type { SharedLens } from '@shared/lens';
import { submitPlanContact } from '../submission';
import { clearContactDraft, loadContactDraft, saveContactDraft } from '../state';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';
import type {
  ContactInfo,
  DiagnosticAnswers,
  OrgProfile,
  OrgTeamSize,
  ScoreResult,
} from '../types';

const TOOL_OPTIONS = [
  'Email',
  'Spreadsheets',
  'Contract management',
  'Matter management',
  'eBilling / spend',
  'Intake form or portal',
  'Workflow / BPM',
  'Document automation',
  'AI review / drafting',
  'None of these',
];

interface Props {
  lens: SharedLens;
  answers: DiagnosticAnswers;
  score: ScoreResult;
}

export function CaptureCard({ lens, answers, score }: Props) {
  const config = usePlanBuilderConfigContext();
  // Lazy initializers so loadContactDraft() runs once on mount, not on every render.
  const [name, setName] = useState(() => String(loadContactDraft().name ?? ''));
  const [email, setEmail] = useState(() => String(loadContactDraft().email ?? ''));
  const [org, setOrg] = useState(() => String(loadContactDraft().organisation ?? ''));
  const [role, setRole] = useState(() => String(loadContactDraft().role ?? ''));
  const [followUp, setFollowUp] = useState<boolean>(() => loadContactDraft().follow_up_ok ?? true);
  const [size, setSize] = useState<OrgTeamSize | ''>(() => loadContactDraft().team_size ?? '');
  const [tools, setTools] = useState<string[]>(() => loadContactDraft().tools ?? []);
  const [satisfaction, setSatisfaction] = useState<1 | 2 | 3 | 4 | 5 | undefined>(
    () => loadContactDraft().tool_satisfaction,
  );
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [doneKind, setDoneKind] = useState<'follow' | 'no-follow'>('follow');
  const [doneGaveQuestions, setDoneGaveQuestions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist whenever any input changes. Reads the latest state values, so
  // there's no one-keystroke lag like the inline persist() pattern.
  useEffect(() => {
    if (done) return;
    saveContactDraft({
      name,
      email,
      organisation: org,
      role,
      follow_up_ok: followUp,
      team_size: size || undefined,
      tools,
      tool_satisfaction: satisfaction,
    });
  }, [name, email, org, role, followUp, size, tools, satisfaction, done]);

  const submit = async () => {
    setError(null);
    if (!name.trim() || !email.trim()) {
      setError('Name and work email are required.');
      return;
    }
    setBusy(true);
    try {
      const contact: ContactInfo = {
        name: name.trim(),
        email: email.trim(),
        organisation: org.trim() || undefined,
        role: role.trim() || undefined,
        follow_up_ok: followUp,
      };
      // Match prototype line 3611: only construct org_profile when at least
      // one opt-out question was answered. Avoids storing empty {} on the
      // backend and prevents the "your input goes into our research"
      // thank-you copy from showing when no input was actually given.
      const orgProfile: OrgProfile | undefined =
        !followUp && (size || tools.length > 0 || satisfaction !== undefined)
          ? {
              team_size: size || undefined,
              tools: tools.length ? tools : undefined,
              tool_satisfaction: satisfaction,
            }
          : undefined;
      await submitPlanContact(lens, answers, score, contact, config, orgProfile);
      clearContactDraft();
      setDoneKind(followUp ? 'follow' : 'no-follow');
      setDoneGaveQuestions(Boolean(orgProfile));
      setDone(true);
    } catch {
      setError("Couldn't send — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const checkIcon = (
    <svg
      width="10"
      height="10"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  if (done) {
    const first = name.trim().split(/\s+/)[0] ?? '';
    const greeting = first ? `Thanks, ${first}.` : 'Thanks.';
    const body =
      doneKind === 'follow'
        ? ' Your plan is on its way to your inbox. We may follow up occasionally with related research — you can opt out any time.'
        : ` Your plan is on its way to your inbox — we won't be in touch beyond that.${
            doneGaveQuestions
              ? ' Your input goes directly into our research on what legal teams are facing.'
              : ''
          }`;
    return (
      <div className="pb-capture-card no-print">
        <div className="pb-capture-header">
          <div className="pb-capture-title">Want this plan in your inbox?</div>
          <div className="pb-capture-sub">
            Get a copy of your plan and the research underpinning it. We'll only
            follow up if you say it's OK below.
          </div>
        </div>
        <div className="pb-capture-body">
          <div className="pb-cap-thanks">
            <div className="pb-cap-thanks-icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.4}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="pb-cap-thanks-text">
              <strong>{greeting}</strong>
              {body}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-capture-card no-print">
      <div className="pb-capture-header">
        <div className="pb-capture-title">Want this plan in your inbox?</div>
        <div className="pb-capture-sub">
          Get a copy of your plan and the research underpinning it. We'll only
          follow up if you say it's OK below.
        </div>
      </div>
      <div className="pb-capture-body">
        <div className="pb-cap-form">
          <div className="pb-cap-field">
            <label htmlFor="pb-cap-name">
              Your name <span className="pb-required">*</span>
            </label>
            <input
              id="pb-cap-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
            />
          </div>
          <div className="pb-cap-field">
            <label htmlFor="pb-cap-email">
              Work email <span className="pb-required">*</span>
            </label>
            <input
              id="pb-cap-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@company.com"
            />
          </div>
          <div className="pb-cap-field">
            <label htmlFor="pb-cap-org">Organisation</label>
            <input
              id="pb-cap-org"
              type="text"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              placeholder="Acme Legal"
            />
          </div>
          <div className="pb-cap-field">
            <label htmlFor="pb-cap-role">Your role</label>
            <input
              id="pb-cap-role"
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Legal Operations Lead"
            />
          </div>

          <label
            className={`pb-cap-check${followUp ? ' on' : ''}`}
            style={{ border: 'none', padding: 0, background: 'transparent' }}
          >
            <input
              type="checkbox"
              checked={followUp}
              onChange={(e) => setFollowUp(e.target.checked)}
            />
            <span className="pb-cap-check-box">{checkIcon}</span>
            <span>
              OK for mot-r to be in touch — research, product updates, or to
              talk about how we could help
            </span>
          </label>

          {!followUp && (
            <div className="pb-cap-optout-questions show">
              <div className="pb-cap-optout-header">
                <strong>
                  If we won't be in touch, would you help us improve the tool?
                </strong>
                Three quick questions in exchange — your answers shape what we
                build next. All optional.
              </div>
              <div className="pb-cap-field">
                <label htmlFor="pb-cap-size">
                  How many people are on your legal team?
                </label>
                <select
                  id="pb-cap-size"
                  value={size}
                  onChange={(e) => setSize(e.target.value as OrgTeamSize | '')}
                >
                  <option value="">Select...</option>
                  <option value="1-5">1–5</option>
                  <option value="6-15">6–15</option>
                  <option value="16-50">16–50</option>
                  <option value="51-200">51–200</option>
                  <option value="200+">200+</option>
                </select>
              </div>
              <div className="pb-cap-field">
                <label>Which of these does your team currently use?</label>
                <div className="pb-cap-checkbox-grid">
                  {TOOL_OPTIONS.map((t) => {
                    const on = tools.includes(t);
                    return (
                      <label key={t} className={`pb-cap-check${on ? ' on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) =>
                            setTools(
                              e.target.checked
                                ? [...tools, t]
                                : tools.filter((x) => x !== t),
                            )
                          }
                        />
                        <span className="pb-cap-check-box">{checkIcon}</span>
                        <span>{t}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="pb-cap-field">
                <label>
                  How satisfied are you with your current tools and operating
                  practices?
                </label>
                <div className="pb-scale-row">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`pb-sbtn${satisfaction === v ? ' sel' : ''}`}
                      onClick={() => setSatisfaction(v as 1 | 2 | 3 | 4 | 5)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <div className="pb-scale-labels">
                  <span>Very dissatisfied</span>
                  <span>Very satisfied</span>
                </div>
              </div>
            </div>
          )}

          <div className="pb-cap-field-hint">
            We'll always send your plan. We won't spam you, and we'll never
            sell or share your details with anyone outside mot-r. Opt out any
            time.
          </div>
          {error && (
            <div className="pb-cap-error" role="alert">
              {error}
            </div>
          )}
          <div className="pb-cap-actions">
            <button
              type="button"
              className="pb-cap-submit"
              onClick={submit}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send me the plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
